import { httpClient } from "../../../lib/http/axios";
import type { ApiEnvelope } from "../../../lib/http/contracts";
import type { PerformanceDetailFilters, PerformanceDetailReport, PerformanceReport } from "../types";
import { mapDetails, mapReport, type PerformanceDetailsRaw, type PerformanceReportRaw } from "../api/adapters";

/** 按月份读取绩效，取消请求和月份核对共同防止旧结果混入。 */
export async function fetchReport(month: string, signal?: AbortSignal): Promise<PerformanceReport> {
  const response = await httpClient.get<ApiEnvelope<PerformanceReportRaw>>("/sales-performance", {
    params: { month }, signal,
  });
  if (response.data.code !== "OK" || !response.data.data || response.data.data.month !== month) {
    throw new Error("绩效查询返回的月份或数据无效，请重新查询");
  }
  return mapReport(response.data.data);
}

/** 下载指定月份的真实 Excel 文件，拒绝错误页面伪装的附件。 */
export async function fetchExport(month: string): Promise<Blob> {
  const response = await httpClient.get<Blob>("/sales-performance/export.xlsx", {
    params: { month }, responseType: "blob",
  });
  if (!(response.data instanceof Blob) ||
      response.data.type !== "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
    throw new Error("未收到有效的 Excel 文件，请稍后重试");
  }
  return response.data;
}

/** 读取一个计次页，响应上下文必须与请求一致。 */
export async function fetchDetails(filters: PerformanceDetailFilters, page: number, pageSize: number,
  signal?: AbortSignal): Promise<PerformanceDetailReport> {
  const response = await httpClient.get<ApiEnvelope<PerformanceDetailsRaw>>("/sales-performance/details", {
    params: { ...filters, page, page_size: pageSize }, signal,
  });
  const data = response.data.data;
  if (response.data.code !== "OK" || !data || data.month !== filters.month ||
      data.user_id !== filters.user_id || data.metric !== filters.metric) {
    throw new Error("明细查询返回的范围或数据无效，请重新查询");
  }
  return mapDetails(data);
}

/** 导出整个明细筛选范围，不发送分页参数。 */
export async function fetchDetailExport(filters: PerformanceDetailFilters): Promise<Blob> {
  const response = await httpClient.get<Blob>("/sales-performance/details/export.xlsx", {
    params: filters, responseType: "blob",
  });
  if (!(response.data instanceof Blob) ||
      response.data.type !== "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
    throw new Error("未收到有效的明细 Excel 文件，请稍后重试");
  }
  return response.data;
}
